import classes from "./BiomarkerCard.module.css";
import ArrowDown from "../../svg/ArrowDown";
import useTheme from "../../../../common/hooks/useTheme";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import { getBiomarkerCategoryColor } from "../../store/helper";
import CustomLineChart from "./CustomLineChart";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Popover } from "@mui/material";
import InfoIcon from "../../svg/InfoIcon";
import CustomTooltip from "../common/CustomTooltip";
import { NO_STATUS_LABEL } from "./usePanelData";

type Props = {
  item: any;
  index: number;
  isClickable?: boolean;
  type?: string;
};
const BiomarkerCard = ({ item, index, isClickable = true, type }: Props) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const biomarkerCategories = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.score.biomarkerCategories
  );
  const scoreCategories = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.score.scoreCategories
  );
  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer
  );
  const biomarkerList = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerList
  );

  let chartData;
  if (type === "blood") {
    chartData = reportData.dashboard.bloodbiomarkers.find(
      (el) => el.id === item.id
    )?.values;
  } else {
    if (item.id === "vo2peak") {
      chartData = reportData.dashboard.metrics.find(
        (el) => el.id === item.id
      )?.values;
    } else {
      chartData = reportData.dashboard.breathbiomarkers.find(
        (el) => el.id === item.id
      )?.values;
      // if (chartData && (item.id === "8_RMR" || item.id === "8_AMR")) {
      //   chartData = chartData.map((el) => {
      //     return { ...el, value: el.value === 100 ? 75 : 25 };
      //   });
      // }
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (parentRef.current && !parentRef.current.contains(target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [item.id]);

  const getMessage = (id: string) => {
    if (!biomarkerList) {
      return;
    }
    const curBiomarker = biomarkerList.find(
      (biomarker: { id: string; name: string; text: ReactNode }) =>
        biomarker.id === id
    );
    return curBiomarker && curBiomarker.text
      ? {
          name: curBiomarker.name,
          text:
            curBiomarker && curBiomarker.text ? (
              <div>{curBiomarker.text}</div>
            ) : null,
        }
      : null;
  };
  let currentBiomarkerItem = getMessage(item.id);

  return (
    <div
      id={`biomarker-card-element-${item.id}`}
      ref={parentRef}
      className={classes.biomarkerCardContainer}
    >
      <div
        className={classes.biomarkerCard}
        style={{
          borderColor: isClickable
            ? isOpen
              ? theme.colors_ds.PRIMARY_ICON_MAIN
              : isHovered
              ? theme.colors_ds.NEUTRAL_BORDER_DEFAULT
              : theme.colors_ds.NEUTRAL_BORDER_WEAK
            : theme.colors_ds.NEUTRAL_BORDER_WEAK,
        }}
      >
        <div
          className={classes.categoryIcon}
          style={{
            backgroundColor: getBiomarkerCategoryColor(
              item.scoreCategory,
              theme,
              "biomarkers"
            ),
          }}
        />
        <div className={classes.biomarkerCardInner}>
          <div className={classes.biomarkerCardDetails}>
            {currentBiomarkerItem && (
              <div style={{ paddingRight: "8px" }}>
                <CustomTooltip
                  message={currentBiomarkerItem.text}
                  label={currentBiomarkerItem.name}
                  type={"large"}
                  customHeight={"20px"}
                >
                  <InfoIcon size={10} />
                </CustomTooltip>
              </div>
            )}
            <div
              className={classes.biomarkerLabel}
              style={{ color: theme.colors_ds.NEUTRAL_TEXT_STRONG }}
            >
              {item.shortname ? item.shortname : item.name}
            </div>
          </div>
          <div className={classes.biomarkerCardDetails}>
            <div
              className={classes.biomarkerValue}
              style={{ color: theme.colors_ds.NEUTRAL_TEXT_STRONG }}
            >
              {item.id !== "8_RMR" && item.id !== "8_AMR"
                ? `${item.value} ${item.unit ?? ""}`
                : item.score === 100
                ? "High"
                : "Low"}
            </div>
            <div
              className={classes.biomarkerCategory}
              style={{ color: theme.colors_ds.NEUTRAL_TEXT_WEAK }}
            >
              {!biomarkerCategories[item.scoreCategory] ||
              String(biomarkerCategories[item.scoreCategory]) === "None"
                ? NO_STATUS_LABEL
                : String(biomarkerCategories[item.scoreCategory])}
            </div>
          </div>
        </div>
        {isClickable && chartData && chartData.length > 0 && (
          <div
            onClick={() => setIsOpen(!isOpen)}
            style={{
              transform: isOpen ? "rotate(180deg)" : "",
              transition: "transform 0.5s ease-in-out",
              height: "14px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <ArrowDown width={14} height={14} />
          </div>
        )}
      </div>

      {isOpen && chartData && (
        <CustomLineChart
          top={
            parentRef.current
              ? `${
                  Math.floor(index / 2) * (parentRef.current.offsetHeight + 8) +
                  (parentRef.current.offsetHeight + 8)
                }px`
              : "0"
          }
          data={chartData}
          id={item.id}
          unit={item.unit !== null ? item.unit : ""}
        />
      )}
    </div>
  );
};

export default BiomarkerCard;
